import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NEVER, of, Subject } from 'rxjs';
import { catchError, delay, takeUntil } from 'rxjs/operators';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { ProposalStatus } from 'src/app/models/enum/proposal-status.enum';
import { Job } from 'src/app/models/job.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { BoardService } from 'src/app/services/sections/job/board.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from '../action-button/models/button-icons.enum';
import { BoardMemberRole } from '../../models/board-members/board-member-role.enum';
import { ToastType } from '../toast/models/toast-type.enum';
import { ApprovalRequest } from './models/approval-request.model';
import { ProposalDocumentInfo } from './models/proposal-document-info.model';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { JobDocumentsService } from 'src/app/services/sections/job/job-documents.service';
import { ProposalService } from 'src/app/services/sections/job/proposal.service';
import { DocumentViewerComponent, DocViewerDocumentDetails } from '../document-viewer/document-viewer.component';

@Component({
	selector: 'board-proposals',
	templateUrl: './proposal-details.component.html',
	styleUrls: ['../stylesheets/job-actions.scss', '../stylesheets/job-main.scss', '../stylesheets/job-inputs.scss', '../stylesheets/document-viewer.scss', './proposal-details.component.scss']
})
export class BoardProposalsComponent implements OnInit, OnDestroy, AfterViewInit {

	@ViewChild('sidenav', { static: true }) public sidenav: MatSidenav;
	@ViewChild('documentViewer', { static: true }) public documentViewer: DocumentViewerComponent;

	public approvalRequest: ApprovalRequest;
	public job: Job;
	public customerPreferences: SettingsPreferences;
	public selectedDocumentDetails: DocViewerDocumentDetails = null;

	public self = this;
	public moment = moment;
	public isLoading = false;
	public isOpened = false;
	public isActionUndergoing = false;

	public readonly ButtonIcons = ButtonIcons;
	public readonly ProposalStatus = ProposalStatus;
	public readonly BoardMemberRole = BoardMemberRole;
	public readonly ApprovalRequest = ApprovalRequest;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly PageType = PageType;

	public unsubscriber$ = new Subject<void>();
	public documentLoadUnsubscriber$ = new Subject<void>();

	public returnUri: string = null;
	public commentFormModel: FormGroup;
	public budgetFormModel: FormGroup;
	public selectedForNegotiation: ProposalDocumentInfo;
	public pageType: PageType;

	public modalAction: 'decline' | 'void' | 'negotiate';

	public get isDocumentMenuVisible(): boolean {
		return this.pageType === PageType.BoardMember && this.isActiveProposal;
	}

	public get isActiveProposal(): boolean {
		return [ProposalStatus.Declined, ProposalStatus.Approved, ProposalStatus.Cancelled].indexOf(this.approvalRequest.proposalStatusId) === -1;
	}

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private boardService: BoardService,
		private proposalService: ProposalService,
		private jobDocumentsService: JobDocumentsService
	) {
		const navigationState = this.router.getCurrentNavigation()?.extras?.state;
		if (navigationState != null && navigationState.returnUri != null) {
			this.returnUri = navigationState.returnUri;
		}

		this.approvalRequest = (this.route.snapshot.data.proposal || {}) as ApprovalRequest;
		this.job = (this.route.snapshot.data.jobDetails || {}) as Job;
	}

	public ngOnInit(): void {

		this.pageType = this.route.snapshot.data.pageType;
		this.customerPreferences = this.route.snapshot.data.customerPreferences as SettingsPreferences;

		this.commentFormModel = new FormGroup({
			comment: new FormControl(null, [Validators.required, Validators.pattern(/\S+/)])	// Pattern validator to prevent space characters only
		});

		this.budgetFormModel = new FormGroup({
			proposalDocumentId: new FormControl(null, [Validators.required]),
			budget: new FormControl(null, [Validators.required, Validators.pattern(/^[0-9]{1,10}(\.[0-9]{1,2})?$/), Validators.required, Validators.min(0.01)])
		});
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
		this.documentLoadUnsubscriber$.next();
		this.documentLoadUnsubscriber$.complete();
	}

	public validateNumberKey(event): boolean {
		return /[0-9\.]/.test(event.key) && (
			!this.budgetFormModel.get('budget').value ||
			/^[0-9]{1,10}(\.[0-9]{0,2})?$/.test(this.budgetFormModel.get('budget').value + event.key)
		);
	}

	public showDocument(document: ProposalDocumentInfo): void {
		this.selectedDocumentDetails = null;

		if (!this.isOpened) {
			this.toggleSlide();
			this.documentViewer.isLoadingFailed = false;
			this.documentViewer.loaderMessage = null;
		}

		const extension = document.name.split('.').reduce((_, current) => current);
		const name = document.description ? document.description + '.' + extension : document.name;

		this.jobDocumentsService
			.getProposalDocument(this.route.snapshot.paramMap.get('token'), document.documentId)
			.pipe(
				takeUntil(this.unsubscriber$),
				takeUntil(this.documentLoadUnsubscriber$),
				catchError(err => {
					this.documentViewer.isLoadingFailed = true;
					this.documentViewer.loaderMessage = 'Could not load document';
					return NEVER;
				})
			)
			.subscribe((content) => {
				this.selectedDocumentDetails = { documentId: document.documentId, content, documentDisplayName: name, documentDownloadName: name };
			});
	}

	public navigateBack(): void {
		if (this.returnUri != null) {
			this.router.navigate([this.returnUri], { relativeTo: this.route.root });
		} else {
			this.router.navigate([this.pageType === PageType.BoardMember ? '../' : '../../'], { relativeTo: this.route.parent });
		}
	}

	public toggleSlide(): void {
		this.isOpened = !this.isOpened;
		this.sidenav.toggle();
	}

	public setNegotiationModal(document: ProposalDocumentInfo): void {
		this.selectedForNegotiation = document;
		this.modalAction = 'negotiate';
		this.commentFormModel.reset();
	}

	public toggleVote(proposalDocumentId: number): void {

		this.proposalService.voteProposal(TokenHelper.getToken(this.route), proposalDocumentId)
			.subscribe(() => {
				const document = this.approvalRequest.documents.find(item => item.proposalDocumentId === proposalDocumentId);

				if (document) {
					document.isVotedByCurrentUser = !document.isVotedByCurrentUser;

					if (document.isVotedByCurrentUser) {
						document.voters.push(this.approvalRequest.currentUser);
					} else {
						const index = document.voters.findIndex(item => item.userAccountId === this.approvalRequest.currentUser.userAccountId);

						if (index > -1) {
							document.voters.splice(index, 1);
						}
					}
				}
			}, () => {
				ToastService.pushToast({
					icon: 'error',
					text: 'Failed completing the action!',
					type: ToastType.Error
				});
			});
	}

	public negotiateDocument(proposalDocument: ProposalDocumentInfo): void {

		const commentForm = this.commentFormModel.getRawValue();
		const documentName = proposalDocument.description || proposalDocument.getName();

		this.proposalService.negotiateProposal(
			TokenHelper.getToken(this.route),
			proposalDocument.proposalDocumentId,
			(`Document: ${documentName}`) + (commentForm.comment ? `\n${commentForm.comment}` : ''))
			.subscribe(() => {
				const document = this.approvalRequest.documents.find(item => item.proposalDocumentId === proposalDocument.proposalDocumentId);

				if (document) {
					document.isNegotiationLocked = true;
					this.approvalRequest.proposalStatusId = ProposalStatus.Negotiation;
				}

				this.commentFormModel.reset();
			}, () => {
				ToastService.pushToast({
					icon: 'error',
					text: 'Failed completing the action!',
					type: ToastType.Error
				});
			});
	}

	public approveProposal(): void {
		const budgetForm = this.budgetFormModel.getRawValue();
		this.proposalService.approveProposal(TokenHelper.getToken(this.route), budgetForm.proposalDocumentId, budgetForm.budget)
			.subscribe(
				() => {
					location.reload();
					this.budgetFormModel.reset();
				}, () => {
					ToastService.pushToast({
						icon: 'error',
						text: 'Failed completing the action!',
						type: ToastType.Error
					});
				});
	}

	public declineProposal(): void {
		this.proposalService.declineProposal(TokenHelper.getToken(this.route), this.approvalRequest.proposalId, this.commentFormModel.getRawValue().comment)
			.subscribe(
				() => {
					location.reload();
					this.commentFormModel.reset();
				}, () => {
					ToastService.pushToast({
						icon: 'error',
						text: 'Failed completing the action!',
						type: ToastType.Error
					});
				});
	}

	public voidProposal(): void {
		this.proposalService.voidProposal(TokenHelper.getToken(this.route), this.approvalRequest.proposalId, this.commentFormModel.getRawValue().comment)
			.subscribe(
				() => {
					location.reload();
					this.commentFormModel.reset();
				}, () => {
					ToastService.pushToast({
						icon: 'error',
						text: 'Failed completing the action!',
						type: ToastType.Error
					});
				});
	}
}
